import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { BarChart3, Bookmark, Download, Play, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useAnalytics,
  useAnalyticsCsv,
  useAnalyticsReportMutations,
  useAnalyticsReports,
  useCurrentPermissions,
  useRunAnalyticsReport,
} from '@/api/queries'
import type { AnalyticsReport } from '@/api/types'
import { BarList, Stat } from '@/features/analytics/blocks'
import { PageHeader } from '@/components/layout/app-shell'
import { formatMoney, formatNumber, formatPercent } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * Le analisi — F7, ANL-09.
 *
 * ## Perché una pagina sola e non sei
 *
 * Le rotte sono sei perché le domande sono sei, ma **chi le guarda le guarda insieme**: «com'è
 * andato il mese» è una frase sola, e sei voci di menù avrebbero costretto a ripetere il periodo sei
 * volte e a ricordarselo nel passaggio. La finestra si scegli una volta in cima e vale per tutto.
 *
 * ## Sei richieste indipendenti
 *
 * Ogni riquadro ha il suo stato: chi guarda gli incassi non aspetta le schede, e una rotta che
 * fallisce non porta via le altre cinque. È anche il motivo per cui i riquadri hanno un errore
 * ciascuno invece di una barra rossa in cima.
 *
 * ## Il periodo si dichiara, e si legge
 *
 * I preset sono tre — trenta giorni, novanta, quest'anno — più due date a mano, e la finestra che il
 * server ha inteso si rilegge **sotto ogni titolo**: un grafico senza il suo periodo è un'immagine
 * che domani non è più vera. Il massimo è 366 giorni e lo dice il server con un `422` sul campo.
 *
 * ## Nessuna libreria di grafici
 *
 * Barre di CSS, per la ragione scritta in `features/analytics/blocks.tsx`. E i numeri che il server
 * manda **nulli** restano «—»: distinguere «zero» da «non misurabile» è metà del lavoro che quelle
 * rotte fanno, e appiattirli qui lo butterebbe via all'ultimo passaggio.
 */
export const Route = createFileRoute('/_app/analisi/')({
  component: AnalyticsPage,
})

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function daysAgo(days: number): string {
  return isoDay(new Date(Date.now() - days * 86_400_000))
}

const PRESETS = [
  { key: '30', label: 'Ultimi 30 giorni', from: () => daysAgo(29), to: () => isoDay(new Date()) },
  { key: '90', label: 'Ultimi 90 giorni', from: () => daysAgo(89), to: () => isoDay(new Date()) },
  {
    key: 'anno',
    label: "Quest'anno",
    from: () => `${String(new Date().getFullYear())}-01-01`,
    to: () => isoDay(new Date()),
  },
] as const

function AnalyticsPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('stats:read')
  const [from, setFrom] = useState(daysAgo(29))
  const [to, setTo] = useState(isoDay(new Date()))
  const csv = useAnalyticsCsv()
  const data = useAnalytics({ from, to, locationId: null, enabled: canRead })

  async function download(area: Parameters<typeof csv.mutateAsync>[0]['area']) {
    try {
      const { blob, filename } = await csv.mutateAsync({ area, from, to, locationId: null })
      // L'ancora si crea qui e non nell'hook: il DOM è del componente, e un hook che lo toccasse
      // non si potrebbe provare. È la stessa divisione dell'esportazione dell'anagrafica.
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  if (!canRead) {
    return (
      <>
        <PageHeader title="Analisi" description="Come è andata la palestra, per periodo." />
        <EmptyState
          icon={<BarChart3 />}
          title="Non hai accesso alle statistiche"
          // Una spiegazione e non una pagina vuota: un elenco vuoto direbbe «non è successo niente».
          description="Le analisi richiedono il permesso «stats:read», che di norma ha chi gestisce la palestra. Chiedilo a un titolare."
        />
      </>
    )
  }

  return (
    <>
      <PageHeader title="Analisi" description="Come è andata la palestra, per periodo." />

      <div className="border-border mb-4 flex flex-wrap items-end gap-2 rounded-lg border p-3">
        {PRESETS.map((preset) => (
          <Button
            key={preset.key}
            variant={from === preset.from() && to === preset.to() ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setFrom(preset.from())
              setTo(preset.to())
            }}
          >
            {preset.label}
          </Button>
        ))}
        <label className="text-muted-foreground ml-auto text-xs">
          Dal
          <input
            type="date"
            value={from}
            max={to}
            onChange={(event) => setFrom(event.target.value)}
            className="border-border ml-1 rounded-md border px-2 py-1 text-sm"
          />
        </label>
        <label className="text-muted-foreground text-xs">
          al
          <input
            type="date"
            value={to}
            min={from}
            onChange={(event) => setTo(event.target.value)}
            className="border-border ml-1 rounded-md border px-2 py-1 text-sm"
          />
        </label>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Iscritti"
          area="members"
          query={data.members}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="A inizio periodo" value={formatNumber(analytics.headcount.atStart)} />
                <Stat label="Alla fine" value={formatNumber(analytics.headcount.atEnd)} />
                <Stat
                  label="Nuovi"
                  value={formatNumber(analytics.headcount.joined)}
                  hint="prima iscrizione"
                />
                <Stat label="Cessati" value={formatNumber(analytics.headcount.ended)} />
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <Stat label="Abbandono" value={formatPercent(analytics.headcount.churnRate)} />
                <Stat
                  label="Durata media"
                  value={
                    analytics.averageMembershipDays === null
                      ? '—'
                      : `${formatNumber(analytics.averageMembershipDays)} gg`
                  }
                  hint="delle iscrizioni concluse nel periodo"
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">Età</h4>
              <BarList
                bands={analytics.composition.byAgeBand}
                emptyLabel="Nessun iscritto attivo."
              />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">Anzianità</h4>
              <BarList
                bands={analytics.composition.byTenureBand}
                emptyLabel="Nessun iscritto attivo."
              />
            </>
          )}
        />

        <Section
          title="Accessi"
          area="access"
          query={data.access}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Ingressi" value={formatNumber(analytics.total)} />
                <Stat label="Persone" value={formatNumber(analytics.distinctMembers)} />
                <Stat label="Per persona" value={formatNumber(analytics.averageVisitsPerMember)} />
                <Stat
                  label="Inattivi"
                  value={formatNumber(analytics.inactiveMembers)}
                  hint="da 30 giorni, oggi"
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">
                Per ora — fuso {analytics.timeZone}
              </h4>
              <BarList bands={analytics.byHour} emptyLabel="Nessun ingresso in questo periodo." />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">Per giorno</h4>
              <BarList
                bands={analytics.byWeekday}
                emptyLabel="Nessun ingresso in questo periodo."
              />
            </>
          )}
        />

        <Section
          title="Incassi"
          area="revenue"
          query={data.revenue}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Stat label="Netto incassato" value={formatMoney(analytics.netCents)} />
                <Stat
                  label="Periodo precedente"
                  value={formatMoney(analytics.previous.netCents)}
                  hint={`${analytics.previous.from} → ${analytics.previous.to}`}
                />
                <Stat
                  label="Variazione"
                  value={
                    analytics.changeRatio === null
                      ? 'primo periodo'
                      : formatPercent(analytics.changeRatio)
                  }
                />
              </div>
              <div className="mt-2">
                <Stat
                  label="Venduto e non pagato"
                  value={formatMoney(analytics.unpaidCents)}
                  hint="documenti non annullati con un residuo"
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">Per metodo</h4>
              <BarList
                bands={analytics.byMethod}
                emptyLabel="Nessun incasso in questo periodo."
                format={formatMoney}
              />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">
                Venduto per prodotto
              </h4>
              <BarList
                bands={analytics.byProduct.slice(0, 8)}
                emptyLabel="Nessuna vendita in questo periodo."
                format={formatMoney}
              />
            </>
          )}
        />

        <Section
          title="Fatturato"
          area="billing"
          query={data.billing}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Imponibile" value={formatMoney(analytics.taxableCents)} />
                <Stat label="IVA" value={formatMoney(analytics.vatCents)} />
                <Stat label="Totale" value={formatMoney(analytics.grossCents)} />
                <Stat
                  label="Competenza futura"
                  value={formatMoney(analytics.deferredCents)}
                  hint="venduto ora, guadagnato dopo"
                />
              </div>
              <div className="mt-2">
                <Stat
                  label="Media per chi ha comprato"
                  value={formatMoney(analytics.averagePerMemberCents)}
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">
                Imponibile per aliquota
              </h4>
              <BarList
                bands={analytics.byVatRate}
                emptyLabel="Nessuna vendita in questo periodo."
                format={formatMoney}
              />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">
                Per natura IVA
              </h4>
              <BarList
                bands={analytics.byVatNature}
                emptyLabel="Nessuna vendita in questo periodo."
                format={formatMoney}
              />
            </>
          )}
        />

        <Section
          title="Corsi"
          area="courses"
          query={data.courses}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Riempimento" value={formatPercent(analytics.fillRate)} />
                <Stat
                  label="Lezioni"
                  value={formatNumber(analytics.sessions)}
                  hint={`${formatNumber(analytics.cancelledSessions)} annullate`}
                />
                <Stat label="Assenze" value={formatNumber(analytics.noShows)} />
                <Stat
                  label="In coda"
                  value={formatNumber(analytics.waitlisted)}
                  hint="serve un altro turno?"
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">
                Posti per disciplina
              </h4>
              <BarList
                bands={analytics.byDiscipline}
                emptyLabel="Nessuna prenotazione in questo periodo."
              />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">
                Per istruttore
              </h4>
              <BarList
                bands={analytics.byInstructor}
                emptyLabel="Nessuna prenotazione in questo periodo."
              />
            </>
          )}
        />

        <Section
          title="Schede"
          area="training"
          query={data.training}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Consegnate" value={formatNumber(analytics.assigned)} />
                <Stat label="Persone" value={formatNumber(analytics.membersWithPlan)} />
                <Stat
                  label="Allenamenti"
                  value={formatNumber(analytics.completedSessions)}
                  hint="chiusi"
                />
                <Stat
                  label="Aderenza media"
                  value={formatPercent(analytics.averageAdherence)}
                  hint="per scheda"
                />
              </div>
              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">
                Esercizi più assegnati
              </h4>
              <BarList
                bands={analytics.topExercises}
                emptyLabel="Nessuna scheda consegnata in questo periodo."
              />
            </>
          )}
        />

        <Section
          title="Piani alimentari"
          area="nutrition"
          query={data.nutrition}
          onDownload={download}
          render={(analytics) => (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Consegnati" value={formatNumber(analytics.assigned)} />
                <Stat label="Persone" value={formatNumber(analytics.membersWithPlan)} />
                <Stat
                  label="Target medio"
                  value={
                    analytics.averageDailyKcalTarget === null
                      ? '—'
                      : `${formatNumber(analytics.averageDailyKcalTarget)} kcal`
                  }
                  hint="scritto dal professionista"
                />
                <Stat
                  label="Chi compila"
                  value={formatNumber(analytics.diary.membersLogging)}
                  hint={`su ${formatNumber(analytics.membersWithPlan)} con un piano`}
                />
              </div>

              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">
                Il diario alimentare
              </h4>
              {/*
                ⚠️ Questo riquadro **non si chiama «aderenza»**, ed è la cosa che va difesa se
                qualcuno lo rinomina. «Giorni compilati su giorni coperti» misura quanto il
                diario viene scritto, non quanto si mangia secondo il piano: chi registra una
                pizza ogni sera è al cento per cento. La riga sotto lo dice a parole, perché un
                numero senza quella frase verrà letto come l'altra cosa.
              */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <Stat
                  label="Giorni compilati"
                  value={
                    analytics.diary.coveredDays === 0
                      ? '—'
                      : formatPercent(analytics.diary.loggedDays / analytics.diary.coveredDays)
                  }
                  hint={`${formatNumber(analytics.diary.loggedDays)} su ${formatNumber(
                    analytics.diary.coveredDays,
                  )} giorni-persona`}
                />
                <Stat label="Righe scritte" value={formatNumber(analytics.diary.entries)} />
                <Stat
                  label="Calorie medie"
                  value={
                    analytics.diary.averageLoggedKcal === null
                      ? '—'
                      : `${formatNumber(analytics.diary.averageLoggedKcal)} kcal`
                  }
                  // Il denominatore accanto alla media, sempre: se è molto più piccolo dei
                  // giorni compilati, quella media parla di pochi giorni.
                  hint={`su ${formatNumber(analytics.diary.fullyMeasuredDays)} giorni interamente misurati`}
                />
              </div>
              <p className="text-muted-foreground mt-2 text-xs">
                «Giorni compilati» dice quanto il diario <em>viene scritto</em>, non quanto si
                mangia secondo il piano. Le calorie medie escludono i giorni con righe senza valori
                nutrizionali: mediarli darebbe un consumo più basso del vero.
              </p>

              <h4 className="text-muted-foreground mt-4 mb-1 text-xs font-medium">Per stato</h4>
              <BarList
                bands={analytics.byStatus}
                emptyLabel="Nessun piano consegnato in questo periodo."
              />
              <h4 className="text-muted-foreground mt-3 mb-1 text-xs font-medium">
                Per professionista
              </h4>
              <BarList
                bands={analytics.byProfessional}
                emptyLabel="Nessun piano consegnato in questo periodo."
              />
            </>
          )}
        />
      </div>

      <SavedReports from={from} to={to} />
    </>
  )
}

const AREA_LABELS: Record<AnalyticsReport['area'], string> = {
  members: 'Iscritti',
  access: 'Accessi',
  courses: 'Corsi',
  revenue: 'Incassi',
  billing: 'Fatturato',
  training: 'Schede',
  nutrition: 'Piani alimentari',
}

const WINDOW_LABELS: Record<AnalyticsReport['windowKind'], string> = {
  last_30_days: 'Ultimi 30 giorni',
  last_90_days: 'Ultimi 90 giorni',
  year_to_date: "Dall'inizio dell'anno",
  previous_month: 'Mese precedente',
  custom: 'Periodo fisso',
}

const SCHEDULE_LABELS: Record<AnalyticsReport['schedule'], string> = {
  none: 'Nessun invio',
  weekly: 'Ogni lunedì',
  monthly: 'Il primo del mese',
}

/**
 * I report salvati — ANL-08.
 *
 * ## Stanno **sotto** le analisi e non in una pagina propria
 *
 * Un report si salva guardando una vista: «questa, ogni lunedì». Metterli altrove avrebbe costretto
 * a ricostruire a memoria l'area e il periodo che si stava guardando — che è esattamente ciò che il
 * report esiste per non far fare.
 *
 * ## Il periodo che si salva è un **modo**, e la schermata lo dice
 *
 * L'elenco mostra «Mese precedente», non due date: è la forma che rende il report ancora vero il
 * mese prossimo, e vederla scritta è ciò che impedisce di chiedersi «di che gennaio parla?».
 *
 * ## Eseguirlo apre i numeri **e la finestra risolta**
 *
 * Il pannello non ridisegna i sei riquadri per un report: mostra la finestra che il server ha
 * calcolato adesso e offre il CSV. Ridisegnarli avrebbe voluto sette viste per sette aree, e la
 * pagina sopra le ha già — un report è una scorciatoia a quelle, non un secondo modo di leggerle.
 */
function SavedReports({ from, to }: { from: string; to: string }) {
  const reports = useAnalyticsReports()
  const mutations = useAnalyticsReportMutations()
  const [opened, setOpened] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [area, setArea] = useState<AnalyticsReport['area']>('revenue')
  const [windowKind, setWindowKind] = useState<AnalyticsReport['windowKind']>('previous_month')
  const [schedule, setSchedule] = useState<AnalyticsReport['schedule']>('none')
  const [email, setEmail] = useState('')
  const run = useRunAnalyticsReport(opened)
  const rows = reports.data?.data ?? []

  async function save() {
    try {
      await mutations.create.mutateAsync({
        name: name.trim(),
        area,
        windowKind,
        // Il periodo fisso prende la finestra che si sta guardando: chi salva «questo» intende
        // quello che ha davanti, e chiederglielo due volte sarebbe la domanda a cui ha già risposto.
        ...(windowKind === 'custom' ? { from, to } : {}),
        ...(schedule === 'none' ? { schedule } : { schedule, recipientEmail: email.trim() }),
      })
      notify({ message: 'Report salvato.', variant: 'success' })
      setCreating(false)
      setName('')
      setEmail('')
    } catch (error) {
      notify({ message: errorMessage(error), variant: 'danger' })
    }
  }

  return (
    <Card className="mt-4">
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div>
          <CardTitle>Report salvati</CardTitle>
          <p className="text-muted-foreground mt-0.5 text-xs">
            Un report salva <strong>il modo</strong> di calcolare il periodo, non il periodo: resta
            vero anche il mese prossimo.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setCreating((value) => !value)
          }}
        >
          <Bookmark className="size-4" /> Salva una vista
        </Button>
      </CardHeader>
      <CardContent>
        {creating ? (
          <div className="border-border mb-3 grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
            <label className="text-xs">
              Nome
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Incassi del mese"
                className="border-border mt-1 w-full rounded-md border px-2 py-1 text-sm"
              />
            </label>
            <label className="text-xs">
              Analisi
              <select
                value={area}
                onChange={(event) => setArea(event.target.value as AnalyticsReport['area'])}
                className="border-border mt-1 w-full rounded-md border px-2 py-1 text-sm"
              >
                {Object.entries(AREA_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              Periodo
              <select
                value={windowKind}
                onChange={(event) => {
                  setWindowKind(event.target.value as AnalyticsReport['windowKind'])
                }}
                className="border-border mt-1 w-full rounded-md border px-2 py-1 text-sm"
              >
                {Object.entries(WINDOW_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              {windowKind === 'custom' ? (
                <span className="text-muted-foreground mt-1 block">
                  Userà il periodo che stai guardando: {from} → {to}
                </span>
              ) : null}
            </label>
            <label className="text-xs">
              Invio per email
              <select
                value={schedule}
                onChange={(event) => {
                  setSchedule(event.target.value as AnalyticsReport['schedule'])
                }}
                className="border-border mt-1 w-full rounded-md border px-2 py-1 text-sm"
              >
                {Object.entries(SCHEDULE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            {schedule === 'none' ? null : (
              <label className="text-xs sm:col-span-2">
                A chi
                <input
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="titolare@palestra.it"
                  className="border-border mt-1 w-full rounded-md border px-2 py-1 text-sm"
                />
              </label>
            )}
            <div className="sm:col-span-2">
              <Button
                size="sm"
                disabled={name.trim() === '' || mutations.create.isPending}
                onClick={() => void save()}
              >
                Salva
              </Button>
            </div>
          </div>
        ) : null}

        {reports.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : reports.isError && reports.data === undefined ? (
          <ErrorState
            message={errorMessage(reports.error)}
            onRetry={() => {
              void reports.refetch()
            }}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Bookmark />}
            title="Nessun report salvato"
            description="Salva una vista che guardi spesso — «gli incassi del mese precedente» — e ritrovala qui, con l'invio per email se serve."
          />
        ) : (
          <ul className="divide-border divide-y">
            {rows.map((report) => (
              <li key={report.id} className="py-2 text-sm">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium">{report.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {AREA_LABELS[report.area]} · {WINDOW_LABELS[report.windowKind]} ·{' '}
                    {SCHEDULE_LABELS[report.schedule]}
                    {report.recipientEmail === null ? null : ` → ${report.recipientEmail}`}
                  </span>
                  <span className="ml-auto flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setOpened(opened === report.id ? null : report.id)
                      }}
                    >
                      <Play className="size-4" /> Esegui
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          try {
                            const { blob, filename } = await mutations.csv.mutateAsync({
                              reportId: report.id,
                              name: report.name,
                            })
                            const url = URL.createObjectURL(blob)
                            const anchor = document.createElement('a')
                            anchor.href = url
                            anchor.download = filename
                            anchor.click()
                            URL.revokeObjectURL(url)
                          } catch (error) {
                            notify({ message: errorMessage(error), variant: 'danger' })
                          }
                        })()
                      }}
                    >
                      <Download className="size-4" /> CSV
                    </Button>
                    <Button
                      variant="danger-outline"
                      size="sm"
                      onClick={() => {
                        void (async () => {
                          try {
                            await mutations.remove.mutateAsync(report.id)
                            if (opened === report.id) setOpened(null)
                          } catch (error) {
                            notify({ message: errorMessage(error), variant: 'danger' })
                          }
                        })()
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </span>
                </div>
                {report.lastSentOn === null ? null : (
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    Ultimo invio: {report.lastSentOn}
                  </p>
                )}
                {opened === report.id ? (
                  <div className="bg-muted/40 mt-2 rounded-md p-2 text-xs">
                    {run.isPending ? (
                      <Skeleton className="h-10 w-full" />
                    ) : run.data === undefined ? (
                      <Alert>Nessun dato.</Alert>
                    ) : (
                      <>
                        {/* La finestra risolta **adesso**: è la riga che rende leggibile un report
                            salvato mesi fa, e il motivo per cui il server la manda. */}
                        <p className="tabular-nums">
                          Periodo risolto: {run.data.window.from} → {run.data.window.to} (
                          {run.data.window.days} giorni)
                        </p>
                        <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap">
                          {JSON.stringify(run.data.analytics, null, 2)}
                        </pre>
                      </>
                    )}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

interface SectionProps<T extends { window: { from: string; to: string; days: number } }> {
  title: string
  area: 'members' | 'access' | 'courses' | 'revenue' | 'billing' | 'training' | 'nutrition'
  query: {
    data: T | undefined
    isPending: boolean
    isError: boolean
    error: unknown
    refetch: () => void
  }
  onDownload: (area: SectionProps<T>['area']) => void
  render: (analytics: T) => React.ReactNode
}

/**
 * Un riquadro: titolo, la finestra che il server ha inteso, il CSV e il contenuto.
 *
 * La finestra si stampa **sotto il titolo di ogni riquadro** e non una volta in cima, e non è una
 * ripetizione inutile: chi fa uno screenshot ne ritaglia uno, e quello screenshot deve dire di che
 * periodo parla.
 */
function Section<T extends { window: { from: string; to: string; days: number } }>({
  title,
  area,
  query,
  onDownload,
  render,
}: SectionProps<T>) {
  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2">
        <div>
          <CardTitle>{title}</CardTitle>
          {query.data === undefined ? null : (
            <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
              {query.data.window.from} → {query.data.window.to} ({query.data.window.days} giorni)
            </p>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            onDownload(area)
          }}
          disabled={query.data === undefined}
        >
          <Download className="size-4" /> CSV
        </Button>
      </CardHeader>
      <CardContent>
        {query.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : query.isError && query.data === undefined ? (
          // Un errore per riquadro e non una barra in cima: cinque analisi su sei possono funzionare,
          // e nasconderle tutte per una sarebbe la scelta peggiore.
          <ErrorState
            message={errorMessage(query.error)}
            onRetry={() => {
              query.refetch()
            }}
          />
        ) : query.data === undefined ? (
          <Alert>Nessun dato.</Alert>
        ) : (
          render(query.data)
        )}
      </CardContent>
    </Card>
  )
}
